import { useContext, useEffect, useState } from "react";
import {
  alpha,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useMutation, useQuery } from "react-query";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "../../api/axios";
import AuthContext from "../../context/AuthProvider";
import logo from "../../utils/images/ridePlus.png";
import { queryClient } from "../../App";
import CustomPinInput from "../../components/customPinInput";
import OtpResendButton from "../../components/otpResendButton";
import { resetOtpCooldownForPhone } from "../../utils/otpCooldownReset";
import { useThemeMode } from "../../context/ThemeContext";
import useTenantBranding from "../../hooks/useTenantBranding";
import { navigatePostAuth } from "../../utils/postAuthNavigation";

function RequestOtp({ openDialog, userInput, setOpenDialog, cooldownHook }) {
  const navigate = useNavigate();
  const location = useLocation();

  const { mode } = useThemeMode();
  const { tenantLogo, hideDefaultBranding } = useTenantBranding();

  const {
    setAuth,
    setPersist,
    redirectUri,
    currentTenant,
    getTenantIdFromUrl,
    setAuthWithTenant,
  } = useContext(AuthContext);
  const tenantIdForOtp = currentTenant?.id || getTenantIdFromUrl();
  const searchParams = new URLSearchParams(location.search);
  const redirectSearchParams = new URLSearchParams(redirectUri?.search || "");
  const clientIdForOtp =
    searchParams.get("client_id") ||
    searchParams.get("clientId") ||
    redirectSearchParams.get("client_id") ||
    redirectSearchParams.get("clientId") ||
    location.state?.client_id ||
    location.state?.clientId;
  const [requestOtp, setRequestOtp] = useState(false);
  const [inputValue, setInputValue] = useState();

  const { enqueueSnackbar } = useSnackbar();

  const {
    isFetching: refecthLoading,
    data: otpData,
    error: otpError,
  } = useQuery(
    ["refetch_otp", userInput, tenantIdForOtp, clientIdForOtp],
    () =>
      axios.get("/otp", {
        headers: {
          "Content-Type": "application/json",
        },
        params: {
          type: "login",
          phone: userInput,
          tenant_id: tenantIdForOtp,
          ...(clientIdForOtp && { client_id: clientIdForOtp }),
        },
      }),
    {
      onSuccess: () => {
        cooldownHook.startCooldown(); // Start cooldown after successful OTP request
        setRequestOtp(false);
      },
      onError: () => setRequestOtp(false),
      enabled: requestOtp,
    }
  );

  const { isLoading, mutate, error } = useMutation((auth) => {
    return axios.post("/login", auth, {
      withCredentials: true,
    });
  });

  // HELPERS
  const onSubmit = async (formData) => {
    // Include tenant_id in login credentials if available
    const tenantId = currentTenant?.id || getTenantIdFromUrl();
    const inputData = {
      ...formData,
      phone: userInput,
      ...(tenantId && { tenant_id: tenantId }),
    };

    setPersist(true);
    setRequestOtp(false);

    return mutate(inputData, {
      onSuccess: (data) => {
        // Reset OTP cooldowns for this specific phone number on successful login
        resetOtpCooldownForPhone(userInput);

        const authData = data?.data?.data;

        if (tenantId) setAuthWithTenant(authData, tenantId);
        else setAuth(authData);

        queryClient.clear();
        navigatePostAuth(navigate, { redirectUri, location });
      },
    });
  };

  useEffect(() => {
    if (otpData) {
      setRequestOtp(false);
    }
  }, [otpData]);
  useEffect(() => {
    if (error) {
      if (error?.response?.data?.error?.field_error?.length > 0) {
        error?.response?.data?.error?.field_error?.map((msg) => {
          return enqueueSnackbar(msg.description || "Network Error!", {
            variant: "error",
            preventDuplicate: true,
            autoHideDuration: 2000,
          });
        });
      } else {
        enqueueSnackbar(
          error?.response?.data?.error?.message ||
          error?.message ||
          "Network Error!",
          {
            variant: "error",
          }
        );
      }
    }
    queryClient.clear();
  }, [enqueueSnackbar, error]);
  useEffect(() => {
    if (otpError) {
      if (otpError?.response?.data?.error?.field_error?.length > 0) {
        otpError?.response?.data?.error?.field_error?.map((msg) => {
          return enqueueSnackbar(msg.description || "Network Error!", {
            variant: "error",
            preventDuplicate: true,
            autoHideDuration: 2000,
          });
        });
      } else {
        enqueueSnackbar(
          otpError?.response?.data?.error?.message ||
          otpError?.message ||
          "Network Error!",
          {
            variant: "error",
          }
        );
      }
      setRequestOtp(false);
    }
  }, [enqueueSnackbar, otpError, setRequestOtp]);
  return (
    <Dialog
      open={openDialog}
      maxWidth="xs"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          boxShadow: theme => mode === 'light'
            ? `0px 8px 32px ${alpha(theme.palette.common.black, 0.1)}`
            : `0px 8px 32px ${alpha(theme.palette.common.black, 0.4)}`,
          borderRadius: 3,
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: "rgba(0, 0, 0, 0.15)",
          backdropFilter: "blur(2px)",
        },
      }}
      PaperProps={{
        sx: {
          width: { xs: "calc(100% - 32px)", sm: "450px" },
          maxWidth: "450px",
          m: { xs: 2, sm: 3 },
        },
      }}
    >
      <DialogContent
        sx={{
          px: 3,
          pt: 3,
          pb: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            mb: 3,
          }}
        >
          {(tenantLogo || !hideDefaultBranding) && (
            <Avatar
              sx={{
                width: { xs: "120px", sm: "140px" },
                height: { xs: "85px", sm: "100px" },
                mb: 2,
                borderRadius: 1,
              }}
              src={tenantLogo || logo}
            />
          )}
          <Typography
            sx={{
              fontSize: { xs: 18, sm: 20 },
              fontWeight: 600,
              color: "text.primary",
              textAlign: "center",
              mb: 1,
            }}
          >
            Verify Your Phone
          </Typography>
        </Box>
        <Typography
          sx={{
            fontSize: { xs: 14, sm: 15 },
            mb: 3,
            textAlign: "center",
            color: "text.secondary",
            lineHeight: 1.6,
            px: 1,
          }}
        >
          Please enter the verification code sent to{" "}
          <strong style={{ color: "primary.main" }}>{userInput}</strong> via SMS
        </Typography>
        <Box sx={{ mb: 3 }}>
          <CustomPinInput
            handleOnSubmit={onSubmit}
            setInputValue={setInputValue}
          />
        </Box>
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            bgcolor: (theme) => theme.palette.mode === 'light' ? "primary.lighter" : alpha(theme.palette.primary.main, 0.1),
            border: "1px solid",
            borderColor: "primary.main",
          }}
        >
          <OtpResendButton
            cooldownHook={cooldownHook}
            onResend={() => setRequestOtp(true)}
            isLoading={refecthLoading}
            size="small"
            showMessage={true}
          />
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          pb: 3,
          pt: 2,
        }}
      >
        <Box
          sx={{
            display: { md: "flex", xs: "none" },
            gap: 1.5,
            width: "100%",
          }}
        >
          <Button
            size="small"
            variant="outlined"
            fullWidth
            onClick={() => setOpenDialog(false)}
            sx={{
              py: 1.5,
              borderColor: "divider",
              color: "text.secondary",
              fontWeight: 600,
              borderRadius: 2,
              textTransform: "none",
              fontSize: 15,
              ":hover": {
                borderColor: "primary.main",
                bgcolor: "action.hover",
              },
            }}
          >
            Back
          </Button>
          <Button
            size="small"
            variant="contained"
            fullWidth
            type="submit"
            onClick={() => onSubmit({ otp: inputValue })}
            disabled={isLoading || refecthLoading || inputValue?.length !== 6}
            sx={{
              py: 1.5,
              bgcolor: "secondary.main",
              fontWeight: 600,
              borderRadius: 2,
              textTransform: "none",
              fontSize: 15,
              boxShadow: "0 3px 5px 2px rgba(29, 36, 38, .2)",
              "&:hover": {
                bgcolor: "secondary.dark",
                transform: { xs: "none", sm: "translateY(-1px)" },
                boxShadow: (theme) => {
                  const shadowColor = alpha(theme.palette.secondary.main, 0.3);
                  return {
                    xs: `0 3px 5px 2px ${shadowColor}`,
                    sm: `0 4px 8px 2px ${shadowColor}`,
                  }
                },
              },
              "&:disabled": {
                bgcolor: 'action.disabledBackground',
                color: 'action.disabled',
              },
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            Continue
            {isLoading && (
              <CircularProgress
                size={24}
                sx={{
                  color: "secondary.contrastText",
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  marginTop: "-12px",
                  marginLeft: "-12px",
                }}
              />
            )}
          </Button>
        </Box>
        <Box
          sx={{
            display: { xs: "flex", md: "none" },
            flexDirection: "column",
            gap: 1.5,
            width: "100%",
          }}
        >
          <Button
            size="small"
            variant="contained"
            fullWidth
            type="submit"
            onClick={() => onSubmit({ otp: inputValue })}
            disabled={isLoading || refecthLoading || inputValue?.length !== 6}
            sx={{
              py: 1.5,
              bgcolor: "secondary.main",
              fontWeight: 600,
              borderRadius: 2,
              textTransform: "none",
              fontSize: 15,
              boxShadow: "0 3px 5px 2px rgba(29, 36, 38, .2)",
              "&:disabled": {
                bgcolor: 'action.disabledBackground',
                color: 'action.disabled',
              },
            }}
          >
            Continue
            {isLoading && (
              <CircularProgress
                size={24}
                sx={{
                  color: "secondary.contrastText",
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  marginTop: "-12px",
                  marginLeft: "-12px",
                }}
              />
            )}
          </Button>
          <Button
            size="small"
            variant="outlined"
            fullWidth
            onClick={() => setOpenDialog(false)}
            sx={{
              py: 1.5,
              borderColor: "divider",
              color: "text.secondary",
              fontWeight: 600,
              borderRadius: 2,
              textTransform: "none",
              fontSize: 15,
              ":hover": {
                borderColor: "primary.main",
                bgcolor: "action.hover",
              },
            }}
          >
            Back
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
export default RequestOtp;
